import React, {useState, useRef, useCallback, useEffect, useReducer} from "react";
import styled from "styled-components";
import type {IFieldAdapter, IFieldPointer} from "../../../data/models/field/Field";
import type {IFilterRow, IFilterRowFilter} from "../../../data/models/filter/Filter";
import type {Color, XyiconFeature} from "../../../generated/api/base";
import {baseDistance, ELLIPSIS, fontSize, fontWeight, radius} from "../styles/styles";
import {colorPalette} from "../styles/colorPalette";
import {ButtonV5} from "../button/ButtonV5";
import {AdvancedFilterFieldV5, CriteriaInputsStyled} from "../filter/advance/AdvancedFilterFieldV5";
import {LogicalSeparators} from "../../../data/models/filter/LogicalSeparators";
import {ToggleOptionV5} from "../details/ToggleOptionV5";
import {ColorSelectorV5} from "../colors/ColorSelectorV5";
import type {ISpotlightRule} from "../../../data/models/View";
import {HorizontalAlignment} from "../../../utils/dom/DomUtils";
import {LogicalSeparator} from "../../../data/models/filter/LogicalSeparator";
import type {FilterOperator} from "../../../data/models/filter/operator/FilterOperator";
import CirclePlusIcon from "../icons/circle-plus.svg?react";
import DragIcon from "../icons/drag.svg?react";
import TrashIcon from "../icons/trash.svg?react";
import {IconButtonV5} from "../interaction/IconButtonV5";
import {AdvancedFilterFieldStyled} from "../filter/advance/AdvancedFilterField.styled";
import {InfoBubbleV5} from "../button/InfoBubbleV5";
import {ReactUtils} from "../../utils/ReactUtils";
import {Debouncer} from "../../../utils/function/Debouncer";
import {useAppStore} from "../../../StateManager";
import {FilterOperatorsV5} from "../filter/operator/FilterOperatorsV5";
import {StringParamV5} from "../filter/advance/controls/StringParamV5";
import {FieldDataTypes} from "../../../data/models/field/FieldDataTypes";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {constructFilterTitle, filterFieldForConditionBasedSpotlight, isFilterComplete} from "./spotlightUtils";

interface ISpotlightConditionBasedRuleV5Props {
	readonly features: XyiconFeature[];
	readonly rule: ISpotlightRule;
	readonly onRuleUpdate?: (ruleId: string, updates: Partial<ISpotlightRule>) => void;
	readonly onRuleDelete?: (ruleId: string) => void;
	readonly operatorFilter?: (operator: FilterOperator) => boolean;
	readonly fieldFilter?: (field: IFieldAdapter) => boolean;
	readonly onExpandChange?: (ruleId: string, isExpanded: boolean) => void;
	readonly multiplePortfolios?: boolean;
	readonly linkedFields?: boolean;
	readonly openInSpaceEditor?: boolean;
	readonly isExpanded?: boolean;
}

export const SpotlightConditionBasedRuleV5 = (props: ISpotlightConditionBasedRuleV5Props) => {
	const {
		features,
		rule,
		onRuleUpdate,
		onRuleDelete,
		operatorFilter,
		fieldFilter,
		multiplePortfolios,
		linkedFields,
		openInSpaceEditor,
		onExpandChange,
		isExpanded = true, // Receive from parent, default to expanded
	} = props;

	const appState = useAppStore((state) => state.appState);
	const [localColor, setLocalColor] = useState<string>(rule.color || "");
	const [showTitleTooltip, setShowTitleTooltip] = useState<boolean>(false);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);

	const colorDebouncerRef = useRef(new Debouncer(50));
	const ruleTitleRef = useRef<HTMLDivElement>(null);
	const ruleTitleTextRef = useRef<HTMLSpanElement>(null);

	/* eslint-disable react-hooks/immutability, react-hooks/set-state-in-effect -- spotlight rule editing mutates the active rule object and syncs local color state from effects. */
	// Ensure conditional rule has at least one filter for editing
	useEffect(() => {
		if (rule.ruleType === "conditional" && (!rule.filters || rule.filters.length === 0)) {
			rule.filters = [{type: "filter", value: {field: "", operator: "" as any, param: ""}}];
			forceUpdate();
		}
	}, [rule]);

	// Update local color when rule color changes
	useEffect(() => {
		if (rule.color && rule.color !== localColor) {
			setLocalColor(rule.color);
		}
	}, [rule.color, localColor]);

	const toggleExpanded = useCallback(() => {
		const newValue = !isExpanded;
		// Notify parent to update state (parent will handle localStorage)
		onExpandChange?.(rule.id, newValue);
	}, [isExpanded, rule.id, onExpandChange]);

	const checkIfTitleTruncated = useCallback(() => {
		if (ruleTitleTextRef.current && ruleTitleRef.current) {
			const scrollWidth = ruleTitleTextRef.current.scrollWidth;
			const clientWidth = ruleTitleTextRef.current.clientWidth;
			const isOverflowing = scrollWidth > clientWidth;
			setShowTitleTooltip(isOverflowing);
		}
	}, []);

	const hideTitleTooltip = useCallback(() => {
		setShowTitleTooltip(false);
	}, []);

	const onIndicatorTypeChange = useCallback(
		(indicatorType: "highlight" | "indicator") => {
			onRuleUpdate?.(rule.id, {indicatorType});
		},
		[onRuleUpdate, rule.id],
	);

	const getFieldRefIds = useCallback((): IFieldPointer[] => {
		let fields = appState.actions.filterActions.getFieldsForFilters(features, undefined, multiplePortfolios) || [];
		fields = fields.filter(filterFieldForConditionBasedSpotlight);

		if (fieldFilter) {
			fields = fields.filter(fieldFilter);
		}
		return fields.map((f) => f.refId);
	}, [appState, features, multiplePortfolios, fieldFilter]);

	const updateRuleAndRefresh = useCallback(() => {
		onRuleUpdate?.(rule.id, {filters: rule.filters});
		forceUpdate();
	}, [onRuleUpdate, rule.id, rule.filters]);

	const onChangeFieldInRule = useCallback(
		(row: IFilterRowFilter, fieldPointer: IFieldPointer) => {
			const field = appState.actions.getFieldByRefId(fieldPointer);
			if (field) {
				const operators = FieldDataTypes.map[field.dataType].operators(field.dataTypeSettings);
				// Sort operators alphabetically by label, then select the first one
				const sortedOperators = [...operators].sort((a, b) => {
					const labelA = FilterOperatorsV5.map[a]?.label || "";
					const labelB = FilterOperatorsV5.map[b]?.label || "";
					return StringUtils.sortIgnoreCase(labelA, labelB);
				});
				row.value.field = fieldPointer;
				row.value.operator = sortedOperators[0];
				row.value.param = "";
				updateRuleAndRefresh();
			}
		},
		[appState, updateRuleAndRefresh],
	);

	const onChangeOperatorInRule = useCallback(
		(row: IFilterRowFilter, operator: FilterOperator) => {
			row.value.operator = operator;
			row.value.param = "";
			updateRuleAndRefresh();
		},
		[updateRuleAndRefresh],
	);

	const shouldDeferParamUpdate = useCallback((row: IFilterRowFilter) => {
		if (!row?.value) {
			return false;
		}

		if (!row.value.operator) {
			return true;
		}

		const operatorConfig = FilterOperatorsV5.map[row.value.operator];
		if (!operatorConfig?.control) {
			return false;
		}

		return operatorConfig.control === StringParamV5;
	}, []);

	const onChangeParamsInRule = useCallback(
		(row: IFilterRowFilter, param: any) => {
			row.value.param = param ?? "";

			if (!shouldDeferParamUpdate(row)) {
				updateRuleAndRefresh();
			}
		},
		[shouldDeferParamUpdate, updateRuleAndRefresh],
	);

	const onBlurParamsInRule = useCallback(() => {
		updateRuleAndRefresh();
	}, [updateRuleAndRefresh]);

	const onRemoveFieldFromRule = useCallback(
		(index: number) => {
			const {filters} = rule;

			if (index >= 0) {
				filters.splice(index, 1);
				if (filters[index]?.type === "separator") {
					filters.splice(index, 1);
				}
			}

			while (filters[filters.length - 1]?.type === "separator") {
				filters.pop();
			}

			if (filters.length === 0) {
				onRuleDelete?.(rule.id);
			} else {
				updateRuleAndRefresh();
			}
		},
		[rule, onRuleDelete, updateRuleAndRefresh],
	);

	const onColorChange = useCallback(
		(color: Color) => {
			const colorString = `#${color.hex ?? ""}`;
			setLocalColor(colorString);
			colorDebouncerRef.current.debounce(() => {
				onRuleUpdate?.(rule.id, {color: colorString});
			});
		},
		[onRuleUpdate, rule.id],
	);

	const onAddRowToRule = useCallback(() => {
		const {filters} = rule;

		if (filters.length > 0) {
			filters.push({type: "separator", value: LogicalSeparator.AND});
		}

		filters.push({
			type: "filter",
			value: {field: "", operator: "" as any, param: ""},
		});

		updateRuleAndRefresh();
	}, [rule, updateRuleAndRefresh]);

	const constructRuleTitle = useCallback(
		(filters: IFilterRow[]): string => {
			if (filters.length === 0) {
				return "Define Spotlight";
			}

			const completeFilters = filters.filter((filter) => {
				if (filter.type === "separator") {
					return true;
				}
				return isFilterComplete(filter);
			});

			return constructFilterTitle(completeFilters, appState) || "Define Spotlight";
		},
		[appState],
	);

	const ruleTitle = constructRuleTitle(rule.filters);
	/* eslint-enable react-hooks/immutability */

	return (
		<SpotlightConditionFilterStyled data-rule-id={rule.id}>
			<RuleContainerStyled>
				<RuleHeaderStyled>
					<div className="color-title-drag-container">
						<div className="drag-handle">
							<DragIcon className="drag-icon" />
						</div>
						<div className="color-lable-container">
							<ColorSelectorV5
								title="Color"
								color={{hex: localColor.replace("#", ""), transparency: 0}}
								onColorChange={onColorChange}
								isTransparencyEnabled={false}
								horizontalAlignment={HorizontalAlignment.outerRight}
							/>
							<div
								ref={ruleTitleRef}
								className={ReactUtils.cls("rule-label", {"rule-label-expanded-space-list": isExpanded && openInSpaceEditor})}
								onClick={toggleExpanded}
								onMouseEnter={checkIfTitleTruncated}
								onMouseLeave={hideTitleTooltip}
								style={{cursor: "pointer", position: "relative"}}
							>
								<span
									ref={ruleTitleTextRef}
									className="rule-label-text"
								>
									{ruleTitle}
								</span>
								{showTitleTooltip && (
									<InfoBubbleV5
										content={ruleTitle}
										style={{maxWidth: "528px"}}
										anchorRef={ruleTitleRef}
									/>
								)}
							</div>
						</div>
					</div>
					<div className="toggle-container">
						{isExpanded && (
							<>
								{openInSpaceEditor && (
									<ToggleOptionV5
										key={`indicator-${rule.id}`}
										options={["Highlight", "Indicator"]}
										render={(option) => option}
										onChange={(value) => onIndicatorTypeChange(value.toLowerCase() as "highlight" | "indicator")}
										selectedOption={rule.indicatorType === "indicator" ? "Indicator" : "Highlight"}
										className="spotlight-toggle-option"
									/>
								)}
								<ToggleOptionV5
									key={`highlight-${rule.id}`}
									options={["Row", "Field"]}
									render={(option) => option}
									onChange={(value) => onRuleUpdate?.(rule.id, {highlightType: value.toLowerCase() as "row" | "field"})}
									selectedOption={rule.highlightType === "field" ? "Field" : "Row"}
									className="spotlight-toggle-option"
								/>
							</>
						)}
						{!isExpanded && onRuleDelete && (
							<IconButtonV5
								IconComponent={TrashIcon}
								onClick={() => onRuleDelete(rule.id)}
								title="Delete"
								className="delete-button"
							/>
						)}
					</div>
				</RuleHeaderStyled>
				{isExpanded && (
					<RuleContentStyled>
						{rule.filters.map((row, index) => {
							if (row.type !== "filter") {
								return null;
							}

							const isFieldEmpty = !row.value.field;
							const isOperatorEmpty = !row.value.operator;
							const operatorTooltipMessage = isFieldEmpty ? "Select a field first" : isOperatorEmpty ? "Select a condition first" : undefined;
							const prevRow = rule.filters[index - 1];

							return (
								<AdvancedFilterFieldV5
									key={`${index}-${row.value.field}`}
									fieldRefIds={getFieldRefIds()}
									filter={row.value}
									onChangeField={(field) => onChangeFieldInRule(row, field)}
									onChangeOperator={(operator) => onChangeOperatorInRule(row, operator)}
									onChangeParams={(params) => onChangeParamsInRule(row, params)}
									onBlurParams={onBlurParamsInRule}
									onRemove={() => onRemoveFieldFromRule(index)}
									operatorFilter={operatorFilter}
									linkedFields={linkedFields}
									disableOperator={isFieldEmpty}
									disableParam={isFieldEmpty || isOperatorEmpty}
									operatorTooltip={operatorTooltipMessage}
								>
									<div>
										{prevRow?.type === "separator" ? (
											<ToggleOptionV5
												key={`${index - 1}-${prevRow.value}`}
												options={LogicalSeparators.array}
												render={(separator) => separator.label}
												onChange={(value) => {
													prevRow.value = value.id;
													updateRuleAndRefresh();
												}}
												selectedOption={LogicalSeparators.array.find((s) => s.id === prevRow.value)?.label}
												className="spotlight-toggle-option"
											/>
										) : (
											<span>Where</span>
										)}
									</div>
								</AdvancedFilterFieldV5>
							);
						})}
						<div className="btn-container">
							<ButtonV5
								label="Add Condition"
								type="tertiary"
								onClick={onAddRowToRule}
								className="add-condition-btn"
								disabled={!rule.filters.every((f) => f.type === "separator" || isFilterComplete(f))}
							>
								<CirclePlusIcon />
							</ButtonV5>
						</div>
					</RuleContentStyled>
				)}
			</RuleContainerStyled>
		</SpotlightConditionFilterStyled>
	);
};

const SpotlightConditionFilterStyled = styled.div`
	position: relative;
	box-sizing: border-box;
	padding-bottom: ${baseDistance.sm};

	&::after {
		content: "";
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: 1px;
		background-color: ${colorPalette.gray.c100};
	}

	&:last-child::after {
		display: none;
	}

	.btn-container {
		padding: ${baseDistance.sm} 0;
		display: flex;
		flex-direction: row;
		justify-content: space-between;

		.add-condition-btn {
			font-size: ${fontSize.md};
			font-weight: ${fontWeight.normal};
			cursor: pointer;
			justify-content: left;
			border: none;

			&:hover {
				color: none;
				background-color: none;
			}
		}
	}
`;

const RuleContainerStyled = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.sm};
	border-radius: ${radius.md};
	background-color: ${colorPalette.white};
	overflow: visible;

	&:hover {
		border-color: ${colorPalette.primary.c500Primary};
	}
`;

const RuleHeaderStyled = styled.div`
	display: flex;
	align-items: center;
	gap: ${baseDistance.sm};
	margin-right: ${baseDistance.md};
	overflow: visible;

	.ColorSelector {
		width: 24px;
		height: 24px;
	}

	.color-title-drag-container {
		width: 100%;
		display: flex;
		align-items: center;
		gap: ${baseDistance.sm};
		margin-left: ${baseDistance.md};
		overflow: visible;
	}

	.rule-label {
		display: flex;
		align-items: center;
		gap: ${baseDistance.xs};
		min-width: 0;
		max-width: 464px;
		overflow: visible;

		&.rule-label-expanded-space-list {
			max-width: 320px;
		}

		.rule-label-text {
			font-size: ${fontSize.md};
			font-weight: ${fontWeight.bold};
			color: ${colorPalette.gray.c950};
			${ELLIPSIS}
			flex: 1;
			min-width: 0;
			line-height: ${baseDistance.md};
		}
	}

	.color-lable-container {
		max-height: 24px;
		display: flex;
		align-items: center;
		gap: ${baseDistance.xs};
	}

	.toggle-container {
		height: 24px;
		display: flex;
		align-items: center;
		gap: ${baseDistance.sm};
		align-items: center;
		justify-content: flex-end;
		width: 100%;

		.spotlight-toggle-option {
			background-color: ${colorPalette.gray.c100};
			padding: ${baseDistance.xxs};

			.toggle-button {
				background-color: ${colorPalette.gray.c100};

				&.active {
					background-color: ${colorPalette.primary.c200Light};
				}
			}
		}

		.IconButton {
			width: 24px;
			height: 24px;

			svg {
				width: 16px;
				height: 16px;
			}
		}
	}

	.toggle-button {
		width: auto;
	}
`;

const RuleContentStyled = styled.div`
	display: flex;
	flex-direction: column;
	margin-left: 48px;
	background-color: ${colorPalette.gray.c50};
	border-radius: ${radius.md};
	padding-left: ${baseDistance.sm};

	${AdvancedFilterFieldStyled} {
		padding: ${baseDistance.sm} 0;
		background-color: transparent;

		.SelectInput {
			background-color: ${colorPalette.white};
			border: none;

			&.disabled {
				color: ${colorPalette.gray.c300};
			}
		}

		.spotlight-toggle-option {
			background-color: ${colorPalette.gray.c100};
			padding: ${baseDistance.xxs};

			.toggle-button {
				background-color: ${colorPalette.gray.c100};
			}

			.toggle-button.active {
				background-color: ${colorPalette.primary.c200Light};
			}
		}

		${CriteriaInputsStyled} {
			grid-template-columns: 54px 156px 128px 200px 20px;

			input {
				background-color: ${colorPalette.white};
				border: none;

				&::placeholder {
					color: ${colorPalette.gray.c300};
				}
			}

			.header {
				width: 54px;
				background-color: transparent;
			}

			.toggle-button {
				width: 24px;
			}

			.toggle-button.active {
				width: 30px;
			}

			.IconButton {
				min-width: 24px;
				width: 24px;
				height: 24px;

				svg {
					width: 16px;
					height: 16px;
				}
			}
		}
	}

	.btn-container {
		margin-left: ${baseDistance.sm};
	}

	.IconButton {
		svg {
			width: 16px;
			height: 16px;
		}
	}
`;
